import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ClipboardList, FileSearch, Calendar, CheckCircle,
  UserPlus, Stethoscope, ArrowRight,
  Shield, DollarSign, Clock, Globe, MessageCircle
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { PUBLIC_PAGE_SEO } from '../data/publicPageSeo';

const PATIENT_STEPS = [
  {
    step: '01',
    icon: ClipboardList,
    title: 'Share Your Care Enquiry',
    desc: 'Tell us what care you are looking for, your location, preferred dates and any communication or travel-support needs through an existing enquiry form or contact channel.',
    detail: 'An enquiry is a request for support, not a confirmed appointment.',
    color: '#1FA7A1',
  },
  {
    step: '02',
    icon: FileSearch,
    title: 'VietClinics Reviews Your Request',
    desc: 'Our coordination team reviews your needs and may ask for further information before approaching selected providers. Options depend on your location and the care requested.',
    detail: 'All diagnoses, treatment decisions and final prices are set by the licensed provider — not by VietClinics.',
    color: '#1E5BA8',
  },
  {
    step: '03',
    icon: Calendar,
    title: 'Providers Confirm Options',
    desc: 'Selected licensed providers review your enquiry and confirm available options, clinical advice, availability and pricing. Timing depends on your enquiry and the provider.',
    detail: 'No payment is required to enquire. VietClinics does not diagnose, treat or set prices.',
    color: '#2ECC71',
  },
  {
    step: '04',
    icon: CheckCircle,
    title: 'We Help Coordinate Next Steps',
    desc: 'Once you choose an option and the provider confirms it, VietClinics helps coordinate the appointment. Interpretation, travel and patient support can be discussed where available.',
    detail: 'Response and appointment times vary by enquiry and provider availability.',
    color: '#F59E0B',
  },
];

const CLINIC_STEPS = [
  {
    step: '01',
    icon: UserPlus,
    title: 'Register Your Clinic',
    desc: 'Submit your clinic details through the existing registration form so our team can review your interest in working with VietClinics. Review times vary.',
    color: '#1FA7A1',
  },
  {
    step: '02',
    icon: Stethoscope,
    title: 'Discuss Your Services',
    desc: 'Discuss your services, location, clinician credentials and patient communication needs with our team. Any partnership arrangements require review and agreement.',
    color: '#1E5BA8',
  },
  {
    step: '03',
    icon: MessageCircle,
    title: 'Review Relevant Enquiries',
    desc: 'Where a coordination arrangement is agreed, providers review relevant patient enquiries and confirm clinical suitability, available appointments and their own pricing.',
    color: '#2ECC71',
  },
  {
    step: '04',
    icon: CheckCircle,
    title: 'Coordinate Agreed Next Steps',
    desc: 'Once the patient and provider agree to proceed, VietClinics helps coordinate practical next steps. The provider remains responsible for clinical care and treatment decisions.',
    color: '#F59E0B',
  },
];

const BENEFITS = [
  { icon: Shield, title: 'Clinical Responsibility', desc: 'Providers assess patients and make all diagnosis and treatment decisions.' },
  { icon: DollarSign, title: 'Provider-Confirmed Pricing', desc: 'Providers confirm their fees and any assessment needed before a final quote.' },
  { icon: Clock, title: 'Availability Confirmation', desc: 'Appointment timing depends on provider availability and the enquiry.' },
  { icon: Globe, title: 'Communication Needs', desc: 'Language and interpretation requirements are discussed case by case.' },
  { icon: MessageCircle, title: 'Enquiry Coordination', desc: 'VietClinics helps relay questions and coordinate agreed practical details.' },
  { icon: ClipboardList, title: 'Agreed Arrangements', desc: 'Submitting clinic details does not guarantee acceptance or patient enquiries.' },
];

const HowItWorksPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{PUBLIC_PAGE_SEO.howItWorks.title}</title>
        <meta name="description" content={PUBLIC_PAGE_SEO.howItWorks.description} />
        <link rel="canonical" href="https://vietclinics.com/how-it-works" />
      </Helmet>
      <Header />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden py-20 md:py-28" style={{ background: '#F8FAFC' }}>
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <div data-testid="hiw-eyebrow" className="inline-flex items-center gap-2 bg-[#1FA7A1]/10 text-[#1FA7A1] text-[11px] font-bold px-4 py-2 rounded-full mb-5 tracking-widest uppercase">
            Enquiry & Coordination
          </div>
          <h1 data-testid="hiw-title" className="font-extrabold text-slate-900 leading-tight mb-5 tracking-tight" style={{ fontSize: 'clamp(2.2rem, 5vw, 3.4rem)' }}>
            How VietClinics Works
          </h1>
          <p data-testid="hiw-intro" className="text-slate-500 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
            {PUBLIC_PAGE_SEO.howItWorks.intro}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <a data-testid="hiw-patients-anchor" href="#for-patients" className="inline-flex items-center gap-2 bg-[#1FA7A1] text-white font-semibold px-6 py-3 rounded-full hover:bg-[#188B86] transition-colors">
              For Patients
            </a>
            <a data-testid="hiw-clinics-anchor" href="#for-clinics" className="inline-flex items-center gap-2 bg-white border-2 border-[#1E5BA8] text-[#1E5BA8] font-semibold px-6 py-3 rounded-full hover:bg-[#1E5BA8]/5 transition-colors">
              For Clinics
            </a>
          </div>
        </div>
      </section>

      {/* ── FOR PATIENTS ── */}
      <section id="for-patients" className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="mb-12">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">For Patients</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              How care enquiries work — in 4 steps
            </h2>
            <p className="text-slate-500 mt-2">No payment or account needed to enquire. Licensed providers confirm all options, advice and pricing.</p>
          </div>

          <div className="space-y-6">
            {PATIENT_STEPS.map((item, i) => (
              <div
                key={item.step}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start bg-white rounded-[24px] p-6 md:p-8 border border-slate-100 transition-all duration-300 hover:-translate-y-0.5"
                style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}
                data-testid={`patient-step-${i + 1}`}
              >
                {/* Step number */}
                <div className="md:col-span-1 flex md:flex-col items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center font-extrabold text-lg text-white flex-shrink-0"
                    style={{ background: item.color }}
                  >
                    {item.step}
                  </div>
                  {i < PATIENT_STEPS.length - 1 && (
                    <div className="hidden md:block w-0.5 h-8 mt-2 mx-auto" style={{ background: `${item.color}30` }} />
                  )}
                </div>
                {/* Icon */}
                <div className="hidden md:flex md:col-span-1 justify-center pt-1">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${item.color}12` }}>
                    <item.icon className="h-5 w-5" style={{ color: item.color }} />
                  </div>
                </div>
                {/* Content */}
                <div className="md:col-span-10">
                  <h3 className="font-extrabold text-slate-900 text-xl mb-2">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed mb-3">{item.desc}</p>
                  <div
                    className="inline-flex items-start gap-2 text-sm font-medium px-4 py-2.5 rounded-xl"
                    style={{ background: `${item.color}10`, color: item.color }}
                  >
                    <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    {item.detail}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/treatments"
              className="inline-flex items-center gap-2 bg-[#1FA7A1] hover:bg-[#188B86] text-white font-bold px-10 py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5"
              style={{ boxShadow: '0 8px 24px rgba(31,167,161,0.25)' }}
              data-testid="hiw-explore-treatments-btn"
            >
              Explore Treatments
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FOR CLINICS ── */}
      <section id="for-clinics" className="py-20" style={{ background: '#F8FAFC' }}>
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="mb-12">
            <p className="text-[11px] font-bold text-[#1E5BA8] uppercase tracking-widest mb-2">For Clinic Owners</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              Discuss Working with VietClinics
            </h2>
            <p data-testid="hiw-clinic-intro" className="text-slate-500 mt-2">A coordination relationship starts with a review of your services and a conversation about how we could work together.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
            {CLINIC_STEPS.map((item) => (
              <div
                key={item.step}
                data-testid={`clinic-step-${item.step}`}
                className="bg-white rounded-[24px] p-6 border border-slate-100 transition-all duration-300 hover:-translate-y-1"
                style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center font-extrabold text-white"
                    style={{ background: item.color }}
                  >
                    {item.step}
                  </div>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${item.color}12` }}>
                    <item.icon className="h-4.5 w-4.5" style={{ color: item.color }} />
                  </div>
                </div>
                <h3 className="font-extrabold text-slate-900 text-lg mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Benefits grid */}
          <div className="bg-white rounded-[28px] p-8 md:p-10 border border-slate-100" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.05)' }}>
            <h3 className="font-extrabold text-slate-900 text-2xl mb-6 text-center">Roles in the Coordination Process</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {BENEFITS.map((b) => (
                <div key={b.title} data-testid={`clinic-role-${b.title.toLowerCase().replace(/\s+/g, '-')}`} className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <b.icon className="h-4.5 w-4.5 text-[#1FA7A1]" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">{b.title}</p>
                    <p className="text-slate-500 text-xs leading-relaxed mt-0.5">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/clinic-registration"
              className="inline-flex items-center gap-2 bg-[#1E5BA8] hover:bg-[#15417B] text-white font-bold px-10 py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5"
              style={{ boxShadow: '0 8px 24px rgba(30,91,168,0.25)' }}
              data-testid="hiw-register-clinic-btn"
            >
              Register Your Clinic
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ CTA ── */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-2xl font-extrabold text-slate-900 mb-4">Still have questions?</h2>
          <p className="text-slate-500 mb-6">Browse our FAQ or reach out to our coordination team with your questions.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/faq"
              data-testid="hiw-faq-link"
              className="inline-flex items-center justify-center gap-2 bg-white border border-slate-200 text-slate-700 font-semibold px-6 py-3 rounded-full hover:border-[#1FA7A1] hover:text-[#1FA7A1] transition-colors"
            >
              View FAQ
            </Link>
            <Link
              to="/contact"
              data-testid="hiw-contact-link"
              className="inline-flex items-center justify-center gap-2 bg-[#1FA7A1] text-white font-semibold px-6 py-3 rounded-full hover:bg-[#188B86] transition-colors"
            >
              Contact Support
            </Link>
          </div>

          {/* Patient Support cross-link */}
          <div className="mt-10 max-w-2xl mx-auto space-y-3" data-testid="howitworks-patient-support-link">
            <Link
              to="/patient-support-vietnam"
              data-testid="hiw-patient-support-link"
              className="group inline-flex items-center justify-between gap-4 w-full bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl px-5 py-4 transition-colors text-left"
            >
              <div>
                <p className="font-extrabold text-slate-900 text-sm">
                  New to VietClinics? Read our Patient Support guide
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Doctor recommendations, translator help, treatment comparisons &amp; coordination
                </p>
              </div>
              <span aria-hidden className="text-[#1FA7A1] font-bold group-hover:translate-x-0.5 transition-transform">→</span>
            </Link>
            <Link
              to="/doctor-recommendation-vietnam"
              className="group inline-flex items-center justify-between gap-4 w-full bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl px-5 py-4 transition-colors text-left"
              data-testid="howitworks-doctor-recommendation-link"
            >
              <div>
                <p className="font-extrabold text-slate-900 text-sm">
                  Need a doctor recommendation in Vietnam?
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Licensed doctors, English-speaking clinics, suitable for your case
                </p>
              </div>
              <span aria-hidden className="text-[#1FA7A1] font-bold group-hover:translate-x-0.5 transition-transform">→</span>
            </Link>
            <Link
              to="/medical-translator-vietnam"
              className="group inline-flex items-center justify-between gap-4 w-full bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl px-5 py-4 transition-colors text-left"
              data-testid="howitworks-medical-translator-link"
            >
              <div>
                <p className="font-extrabold text-slate-900 text-sm">
                  Need a medical translator or communication support?
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Appointment translation, question prep, treatment plan &amp; aftercare explanation
                </p>
              </div>
              <span aria-hidden className="text-[#1FA7A1] font-bold group-hover:translate-x-0.5 transition-transform">→</span>
            </Link>
            <Link
              to="/how-to-choose-doctor-vietnam"
              className="group inline-flex items-center justify-between gap-4 w-full bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl px-5 py-4 transition-colors text-left"
              data-testid="howitworks-how-to-choose-link"
            >
              <div>
                <p className="font-extrabold text-slate-900 text-sm">
                  Want a practical guide to choosing a doctor?
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Comparison criteria, red flags, the 10 questions to ask
                </p>
              </div>
              <span aria-hidden className="text-[#1FA7A1] font-bold group-hover:translate-x-0.5 transition-transform">→</span>
            </Link>
            <Link
              to="/medical-tourism-support-vietnam"
              className="group inline-flex items-center justify-between gap-4 w-full bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl px-5 py-4 transition-colors text-left"
              data-testid="howitworks-medical-tourism-support-link"
            >
              <div>
                <p className="font-extrabold text-slate-900 text-sm">
                  Travelling to Vietnam for treatment?
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Doctor recommendations, online consults, translator help, travel &amp; aftercare
                </p>
              </div>
              <span aria-hidden className="text-[#1FA7A1] font-bold group-hover:translate-x-0.5 transition-transform">→</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
